import React, { createContext, useContext, ReactNode } from 'react';
import { nuevoId } from '../utils/nuevoId';
import { Project, ProjectStatus } from '../types';
import { getEstimateDoc, getWorkspaceId } from '../services/database';
import {
    useProjectsQuery, useAddProject, useUpdateProject, useDeleteProject, useTransitionProjectStatus
} from '../hooks/useProjectQueries';
import { supabase } from '../services/supabase';
import { geocodeAddress } from '../utils/geomath';

export type AddProjectWarning = 'estimate-seeding-failed' | 'geocoding-failed';

export interface AddProjectResult {
    projectId: string;
    warnings: AddProjectWarning[];
}

interface ProjectsContextType {
    projects: Project[];
    /** True while the initial fetch is in flight. Used by deep-linked routes to show a spinner. */
    isLoadingProjects: boolean;
    /**
     * La consulta TERMINÓ BIEN y `projects` es el censo de verdad del taller.
     *
     * Hace falta aparte de `isLoadingProjects` porque ese es el `isLoading` de
     * React Query, que vale `false` en dos casos donde `projects` es `[]` y no
     * sabemos nada: con la consulta deshabilitada (sin sesión o sin taller
     * resuelto todavía) y con la consulta en error tras los reintentos.
     *
     * Quien pregunte «¿esta obra existe?» tiene que distinguir «no existe» de
     * «todavía no sé»: dando la lista vacía por censo bueno, TODO fichaje con
     * obra se marcaba como obra borrada — la semana entera en ámbar y un
     * `Billable=orphaned` escrito en el export de nómina.
     */
    projectsLoaded: boolean;
    addProject: (project: Omit<Project, 'id' | 'assignedCrewIds' | 'completionPercent' | 'tasks' | 'materialStatus' | 'changeOrders' | 'notes' | 'estimatedLaborHours' | 'expenses' | 'invoices'>) => Promise<AddProjectResult>;
    updateProjectStatus: (id: string, status: ProjectStatus) => void;
    updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
    deleteProject: (id: string) => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export const useProjects = () => {
    const context = useContext(ProjectsContext);
    if (!context) {
        throw new Error('useProjects must be used within a ProjectsProvider');
    }
    return context;
};

export const ProjectsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // --- QUERIES & MUTATIONS (delegated to standalone hooks) ---
    const { data: projects = [], isLoading: isLoadingProjects, isSuccess: projectsLoaded } = useProjectsQuery();
    const addProjectMut = useAddProject();
    const updateProjectMut = useUpdateProject();
    const deleteProjectMut = useDeleteProject();
    const transitionStatusMut = useTransitionProjectStatus();

    // --- ADD PROJECT (complex business logic preserved) ---
    const addProject = async (
        projectData: Omit<Project, 'id' | 'assignedCrewIds' | 'completionPercent' | 'tasks' | 'materialStatus' | 'changeOrders' | 'notes' | 'estimatedLaborHours' | 'expenses' | 'invoices'>
    ): Promise<AddProjectResult> => {
        const warnings: AddProjectWarning[] = [];
        let estimatedHours = 0;
        let initialMaterials: { estimateItemId: string, status: string, poNumber?: string }[] = [];
        let generatedTasks: any[] = [];
        let siteCoordinates: { lat: number; lng: number } | undefined = undefined;
        let geofenceRadiusFt: number | undefined = undefined;

        // --- 1. Pull data from the linked Estimate (only if an estimate is linked) ---
        if (projectData.estimateId) {
            try {
                const linkedEstimate = await getEstimateDoc(projectData.estimateId);
                if (linkedEstimate && linkedEstimate.items && Array.isArray(linkedEstimate.items)) {
                    estimatedHours = linkedEstimate.items
                        .filter((i: any) => i.type === 'Labor')
                        .reduce((sum: number, i: any) => sum + (i.quantity || 0), 0);

                    // Materials carry their own description + vendor going forward (5.3 schema cleanup)
                    initialMaterials = linkedEstimate.items
                        .filter((i: any) => i.type === 'Material')
                        .map((i: any) => ({
                            estimateItemId: i.id || '',
                            // Must match a ProjectMaterials board column or the row
                            // renders in no column at all. 'Needs Ordering' is the
                            // intake state; 'Pending' matched nothing and hid seeds.
                            status: 'Needs Ordering',
                            description: i.description,
                            quantity: i.quantity,
                            cost: i.cost,
                        }));

                    const scopeItems = linkedEstimate.items.filter(
                        (i: any) => i.description && i.description.trim()
                    );

                    if (scopeItems.length > 0) {
                        // Consolidate to <=12 tasks: group by category, then by zoneId, falling
                        // back to one-per-item only if both keys are absent. Keeps the linkage
                        // back to estimate item IDs so completing the task can flip materials.
                        const TASK_CAP = 12;
                        const groupKeyOf = (item: any): string => {
                            return (item.category && String(item.category).trim())
                                || (item.zoneId && String(item.zoneId).trim())
                                || `item-${item.id}`;
                        };

                        const groups = new Map<string, any[]>();
                        scopeItems.forEach((item: any) => {
                            const key = groupKeyOf(item);
                            const arr = groups.get(key) || [];
                            arr.push(item);
                            groups.set(key, arr);
                        });

                        let groupedTasks: any[] = [];
                        if (groups.size <= TASK_CAP) {
                            groupedTasks = Array.from(groups.entries()).map(([key, items], index) => {
                                const description = items.length === 1
                                    ? items[0].description
                                    : `${key} — ${items.length} items`;
                                return {
                                    id: nuevoId(),
                                    description,
                                    isCompleted: false,
                                    priority: index < Math.ceil(groups.size / 3) ? 'High' : 'Medium',
                                    phase: 'Execution' as const,
                                    estimateItemIds: items.map((i: any) => i.id).filter(Boolean),
                                };
                            });
                        } else {
                            // Fallback: one task per item, but capped at TASK_CAP
                            groupedTasks = scopeItems.slice(0, TASK_CAP).map((item: any, index: number) => ({
                                id: nuevoId(),
                                description: item.description,
                                isCompleted: false,
                                priority: index < Math.ceil(TASK_CAP / 3) ? 'High' : 'Medium',
                                phase: 'Execution' as const,
                                estimateItemIds: item.id ? [item.id] : [],
                            }));
                        }
                        generatedTasks = groupedTasks;
                    }
                } else if (!linkedEstimate) {
                    warnings.push('estimate-seeding-failed');
                }
            } catch (e) {
                console.error('[addProject] Failed to fetch linked estimate', e);
                warnings.push('estimate-seeding-failed');
            }
        }

        // --- 2. Fetch client address and geocode for geofence ---
        if (projectData.clientId) {
            try {
                const { data: clientData } = await supabase
                    .from('clients')
                    .select('address')
                    .eq('id', projectData.clientId)
                    .eq('workspace_id', getWorkspaceId())
                    .single();

                const address = clientData?.address?.trim();
                if (address) {
                    // Shared JS-API-first geocoder (utils/geomath.ts). The previous
                    // inline REST call REQUEST_DENIED'd once the Maps key was
                    // referrer-restricted, silently dropping geofences on every
                    // new project — the JS geocoder honors referrer restrictions.
                    const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '').replace(/['"]/g, '').trim();
                    try {
                        const loc = await geocodeAddress(address, apiKey || undefined);
                        if (loc) {
                            siteCoordinates = loc;
                            geofenceRadiusFt = 300;
                        } else {
                            warnings.push('geocoding-failed');
                        }
                    } catch (geoErr) {
                        console.warn('[addProject] Geocoding failed:', geoErr);
                        warnings.push('geocoding-failed');
                    }
                }
                // No address on client: silent skip (user didn't provide one)
            } catch (e) {
                console.error('[addProject] Failed to fetch client for geocoding', e);
                // Not a user-facing concern; client may legitimately not exist yet
            }
        }

        // --- 3. Fallback: generic starter tasks if seeding produced none ---
        if (generatedTasks.length === 0) {
            generatedTasks = [
                { id: nuevoId(), description: 'Site Walkthrough & Safety Check', isCompleted: false, priority: 'High' },
                { id: nuevoId(), description: 'Material Verification', isCompleted: false, priority: 'Medium' },
                { id: nuevoId(), description: 'Equipment Staging', isCompleted: false, priority: 'Medium' }
            ];
        }

        const newProject = {
            ...projectData,
            estimatedLaborHours: estimatedHours,
            status: projectData.status || ('Scheduled' as ProjectStatus),
            assignedUserIds: [],
            assignedCrewIds: [],
            completionPercent: 0,
            tasks: generatedTasks,
            materialStatus: initialMaterials,
            changeOrders: [],
            notes: [],
            expenses: [],
            invoices: [],
            ...(siteCoordinates ? { siteCoordinates, geofenceRadiusFt } : {})
        };

        const docId = await addProjectMut.mutateAsync(newProject);
        if (!docId || typeof docId !== 'string') {
            throw new Error('Project creation returned invalid ID');
        }

        return { projectId: docId, warnings };
    };

    const updateProject = async (id: string, updates: Partial<Project>) => {
        await updateProjectMut.mutateAsync({ id, updates });
    };

    const updateProjectStatus = (id: string, status: ProjectStatus) => {
        // Atomic server-side transition: stamps the lifecycle timestamp and, on
        // Completed, sets completion_percent=100 / end_date. Fire-and-forget; the
        // global mutation backstop surfaces any failure.
        transitionStatusMut.mutate({ id, status });
    };

    const deleteProject = async (id: string) => {
        await deleteProjectMut.mutateAsync(id);
    };

    return (
        <ProjectsContext.Provider value={{ projects, isLoadingProjects, projectsLoaded, addProject, updateProject, updateProjectStatus, deleteProject }}>
            {children}
        </ProjectsContext.Provider>
    );
};
